import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, ArrowLeft, Lock } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../config/api';
import { startMicrosoftLogin } from '../lib/microsoftLogin';

// The last /auth/microsoft/config answer, so the right sign-in shows at once.
const SIGN_IN_CONFIG_KEY = 'aflo_signin_config';
const readSignInConfig = () => {
  try { return JSON.parse(localStorage.getItem(SIGN_IN_CONFIG_KEY) || 'null'); } catch (_) { return null; }
};

// Microsoft's four-square mark, for the "Sign in with Microsoft" button.
const MicrosoftMark = () => (
  <svg viewBox="0 0 21 21" className="h-4 w-4" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // 2FA state
  const [stage, setStage] = useState('credentials'); // 'credentials' | '2fa' | 'password_reset'
  const [challengeToken, setChallengeToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();
  // What the API offers: Microsoft sign-in (Acuity tenant only) and/or email +
  // password. The sign-in options wait for the answer — drawing the password
  // form first and then swapping it flashed the old sign-in. The last answer is
  // kept in this browser, so after the first visit they show at once (and are
  // refreshed behind). If the API can't answer, the password form shows.
  const [ms, setMs] = useState(readSignInConfig);
  const [cfgReady, setCfgReady] = useState(() => readSignInConfig() !== null);
  const [msBusy, setMsBusy] = useState(false);
  useEffect(() => {
    api.get('/auth/microsoft/config')
      .then((r) => {
        setMs(r.data);
        try { localStorage.setItem(SIGN_IN_CONFIG_KEY, JSON.stringify(r.data)); } catch (_) { /* private mode */ }
      })
      .catch(() => { if (readSignInConfig() === null) setMs(null); })
      .finally(() => setCfgReady(true));
  }, []);
  const passwordLogin = !ms || ms.password_login !== false;
  // Guests (outside Acuity) can't use Acuity's Microsoft sign-in, so they keep
  // the email + password form even when staff are Microsoft-only.
  const [guestMode, setGuestMode] = useState(false);
  const showPasswordForm = passwordLogin || guestMode;

  const signInWithMicrosoft = async () => {
    setMsBusy(true);
    try {
      await startMicrosoftLogin(ms);
    } catch (e) {
      setMsBusy(false);
      toast({ title: 'Error', description: 'Could not open Microsoft sign-in', variant: 'destructive' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setChallengeToken(result.challenge_token);
        setStage('2fa');
        toast({ title: 'Enter 2FA code', description: 'Open your authenticator app' });
      } else {
        toast({ title: 'Success', description: 'Logged in successfully!' });
        navigate('/workspaces');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'password_reset_required') {
        setStage('password_reset');
      } else if (detail === 'account_permanently_locked') {
        toast({
          title: 'Account locked',
          description: 'Your account has been locked. Please contact your administrator to unlock it.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: detail || 'Failed to login',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (twoFactorCode.length < 6) return;
    setLoading(true);
    try {
      await verify2FA(challengeToken, twoFactorCode.trim());
      toast({ title: 'Success', description: 'Logged in successfully!' });
      navigate('/workspaces');
    } catch (error) {
      toast({
        title: 'Invalid code',
        description: error.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStage('credentials');
    setTwoFactorCode('');
    setChallengeToken('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center mb-4">
            <img src="/acuity-logo.png" alt="Acuity Professional" className="h-12 object-contain" data-testid="login-logo" />
          </div>
          <p className="-mt-4 text-center text-xs font-medium uppercase tracking-wider text-gray-400" data-testid="login-internal-only">
            For internal use only
          </p>
          {stage === 'credentials' && (
            <>
              <CardTitle className="text-2xl text-center">Welcome back</CardTitle>
              <CardDescription className="text-center">
                {!cfgReady ? '\u00a0'
                  : (ms?.enabled && !passwordLogin
                    ? 'Sign in with your Acuity Professional Microsoft account'
                    : 'Sign in to your Acuity Work Management account')}
              </CardDescription>
            </>
          )}
          {stage === '2fa' && (
            <>
              <div className="flex items-center justify-center">
                <Shield className="h-8 w-8 text-orange-500" />
              </div>
              <CardTitle className="text-2xl text-center">Two-factor verification</CardTitle>
              <CardDescription className="text-center">
                Enter the 6-digit code from your authenticator app
              </CardDescription>
            </>
          )}
          {stage === 'password_reset' && (
            <>
              <div className="flex items-center justify-center">
                <Lock className="h-8 w-8 text-orange-500" />
              </div>
              <CardTitle className="text-2xl text-center">Password update required</CardTitle>
              <CardDescription className="text-center">
                Your password must be updated before you can sign in
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          {stage === 'credentials' && !cfgReady && (
            <div className="h-10 w-full animate-pulse rounded-md bg-gray-100" aria-label="Loading sign-in options" data-testid="login-options-loading" />
          )}
          {stage === 'credentials' && cfgReady && ms?.enabled && (
            <div className="space-y-4">
              <Button type="button" variant="outline" className="w-full gap-2 border-gray-300 font-medium text-gray-800"
                onClick={signInWithMicrosoft} disabled={msBusy} data-testid="login-microsoft">
                <MicrosoftMark /> {msBusy ? 'Opening Microsoft…' : 'Sign in with Microsoft'}
              </Button>
              {showPasswordForm ? (
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="h-px flex-1 bg-gray-200" /> {passwordLogin ? 'or use your password' : 'guest sign-in'} <span className="h-px flex-1 bg-gray-200" />
                </div>
              ) : (
                <button type="button" onClick={() => setGuestMode(true)} data-testid="login-guest-toggle"
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-800">
                  Guest? Sign in with your email
                </button>
              )}
            </div>
          )}

          {stage === 'credentials' && cfgReady && showPasswordForm && (
            <form onSubmit={handleSubmit} className={`space-y-4 ${ms?.enabled ? 'mt-4' : ''}`}>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-orange-600 hover:text-orange-700 font-medium" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
          )}

          {stage === '2fa' && (
            <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="login-2fa-form">
              <div className="space-y-2">
                <Label htmlFor="2fa-code">6-digit code</Label>
                <Input
                  id="2fa-code"
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 8))}
                  placeholder="123456"
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="login-2fa-code"
                />
                <p className="text-xs text-gray-500 text-center">
                  You can also use a backup code if you've lost access to your app.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                disabled={loading || twoFactorCode.length < 6}
                data-testid="login-2fa-submit"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleBack}
                data-testid="login-2fa-back"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            </form>
          )}
          {stage === 'password_reset' && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800 mb-3">
                  Your current password doesn't meet our security requirements. Reset it to regain access.
                </p>
                <p className="text-xs font-medium text-orange-700 mb-1">New password must have:</p>
                <ul className="text-xs text-orange-700 list-disc list-inside space-y-0.5">
                  <li>At least 12 characters</li>
                  <li>One uppercase letter (A–Z)</li>
                  <li>One lowercase letter (a–z)</li>
                  <li>One number (0–9)</li>
                  <li>One special character (!@#$ etc.)</li>
                </ul>
              </div>
              <Button
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                onClick={() => navigate('/forgot-password', { state: { email } })}
              >
                Send password reset link
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setStage('credentials')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to sign in
              </Button>
            </div>
          )}
          {stage === 'credentials' && cfgReady && (
            <div className="mt-4 space-y-1 text-center text-sm">
              {!ms?.enabled && (
                <div>
                  Don't have an account?{' '}
                  <Link to="/register" className="text-orange-600 hover:text-orange-700 font-medium">
                    Sign up
                  </Link>
                </div>
              )}
              <div className="text-gray-500">
                Not at Acuity Professional?{' '}
                <Link to="/guest-access" className="text-orange-600 hover:text-orange-700 font-medium" data-testid="login-guest-request">
                  Request guest access
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
